// @givmo/board — connecting a calendar: the OAuth leg, start to finish (CBG-CAL1 slice 1d; backend
// contract `org-integrations.md` §4, "the console finishes the OAuth leg").
//
// ══ THE SHAPE ═══════════════════════════════════════════════════════════════════════════════════
//
//   1. START. `POST /org/{org_ref}/integrations/oauth/start` mints a single-use `state` and answers
//      the provider's authorization URL. Its `redirect_uri` is THIS console's return page —
//      `<origin><Board mount>/calendar/connected` (`calendarConnectRedirectUri`) — which the operator
//      registers per console host and environment with the provider and in core's
//      `ORG_INTEGRATIONS_OAUTH_REDIRECT_URIS` (CBG-CAL2). Core matches it EXACTLY, so changing the path
//      is an operator change in every environment, not a refactor.
//   2. CONSENT, in a pop-up first. The consent screen opens in its own window so the page that asked
//      — the schedule dialog with its unsaved fields, a settings card — stays as it is (the README's
//      build note). The window is opened blank, DISOWNED (`opener = null`) and only then sent to the
//      provider, so no provider page ever holds a handle on the signed-in console (reverse
//      tabnabbing; `sso-test-session.ts` records why that handle must not exist).
//   3. THE RETURN. The provider sends the window to the return page with `code` and `state` (or
//      `error`). With no opener to message, the page says so on a same-origin `BroadcastChannel`; the
//      dialog that started THIS flow (it knows the `state` from the authorization URL) answers, takes
//      the code and closes the window. It then finishes the flow itself:
//      `POST …/oauth/complete {state, code, redirect_uri}` under the signed-in user, which core binds
//      to the same user twice (the state and the session), and the SAME dialog shows the answer.
//   4. NO POP-UP. When the browser blocks the window (or has no `BroadcastChannel`), the whole tab
//      goes to the provider instead (`start` carries `return_path`, the page that asked). Nothing in
//      the tab answers the return page, so it finishes the flow itself and shows the answer there; its
//      Done goes back to `return_path`. A dialog with unsaved fields keeps them in this tab's
//      `sessionStorage` first (`keepScheduleDraft`), and its page restores them on the way back.
//
// The provider is Google (`google_calendar`) in slice 1. Every answer is one of the dialog's states:
// connected, refused at Google, blocked by a Workspace administrator, too slow (`oauth_state_invalid`:
// one answer for every cause, so a state is no oracle), the calendar box unticked
// (`insufficient_scope`), or one of core's other typed refusals.

import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import type { OrgIntegrationOAuthCompleteOut } from '@givmo/api-client';
import { BOARD_MEETINGS_PURPOSE } from './calendar-queries';

/** The return page, relative to Board's mount. Registered with the provider per console host. */
export const CALENDAR_CONNECT_RETURN_PAGE = '/calendar/connected';

/** The provider slice 1 connects. */
export const CALENDAR_PROVIDER = 'google_calendar';

/** The same-origin channel the return page and the dialog meet on. */
export const CALENDAR_CONNECT_CHANNEL = 'givmo.board.calendar-connect';

/** The window's name, so a second click reuses the one window rather than stacking them. */
export const CALENDAR_CONNECT_WINDOW = 'givmo-board-calendar-connect';

/** How long the return page waits for the dialog that started the flow to answer. */
export const RETURN_ACK_WAIT_MS = 800;

/**
 * The redirect URI for this deployment: the running origin, Board's mount and the return page.
 * Derived from `location.origin`, never a build constant, so it can't disagree with the console the
 * person is on (the `ssoTestRedirectUri` reasoning).
 */
export function calendarConnectRedirectUri(
  basePath: string,
  origin: string = globalThis.location?.origin ?? '',
): string {
  return `${origin}${basePath}${CALENDAR_CONNECT_RETURN_PAGE}`;
}

/** The `state` an authorization URL carries, which identifies the flow it starts. */
export function stateOf(authorizationUrl: string): string | null {
  try {
    const value = new URL(authorizationUrl).searchParams.get('state');
    return value !== null && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

/** What the return page tells the dialog, and the dialog's answer. */
export type ConnectMessage =
  | { type: 'return'; state: string; code: string | null; error: string | null }
  | { type: 'ack'; state: string };

/** Read a message off the channel; anything else is not ours and is ignored. */
export function readConnectMessage(value: unknown): ConnectMessage | null {
  if (typeof value !== 'object' || value === null) return null;
  const message = value as Record<string, unknown>;
  if (typeof message.state !== 'string' || message.state.length === 0) return null;
  if (message.type === 'ack') return { type: 'ack', state: message.state };
  if (message.type !== 'return') return null;
  return {
    type: 'return',
    state: message.state,
    code: typeof message.code === 'string' && message.code.length > 0 ? message.code : null,
    error: typeof message.error === 'string' && message.error.length > 0 ? message.error : null,
  };
}

/** Whether this browser can carry the pop-up's answer back (otherwise the tab itself goes). */
export function canUsePopup(): boolean {
  return typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined';
}

/** The dialog's answer after the provider: one of its drawn states, or a typed refusal's words. */
export type ConnectOutcome =
  | { kind: 'connected'; connection: OrgIntegrationOAuthCompleteOut }
  | { kind: 'refused' }
  | { kind: 'workspace-blocked' }
  | { kind: 'expired' }
  | { kind: 'unticked' }
  | { kind: 'not-connected'; code: string }
  | { kind: 'failed'; message: string | null };

/** Core's typed refusals with words of their own (the build's B6), keyed by `error_code`. */
const NOT_CONNECTED_CODES: Readonly<Record<string, string>> = {
  different_account: 'different_account',
  account_already_connected: 'account_already_connected',
  personal_connection_exists: 'personal_connection_exists',
  board_account_exists: 'board_account_exists',
  no_refresh_token: 'no_refresh_token',
  // Two refusals no screen names; they read as Google not finishing (the orchestrator's B6).
  oauth_exchange_failed: 'no_refresh_token',
  redirect_uri_not_allowed: 'no_refresh_token',
  provider_busy: 'provider_busy',
};

/**
 * The provider's own `error` on the return. A Workspace administrator's block has its own screen
 * (when Google sends the person back with it); any other is the person, or Google, not giving
 * access — nothing changed either way.
 */
export function outcomeOfProviderError(error: string): ConnectOutcome {
  return error === 'admin_policy_enforced' ? { kind: 'workspace-blocked' } : { kind: 'refused' };
}

/** A failed `complete`, as the dialog says it. */
export function outcomeOfCompleteFailure(error: unknown): ConnectOutcome {
  if (!isGivmoError(error)) return { kind: 'failed', message: null };
  const code = firstErrorCode(error);
  if (code === 'oauth_state_invalid') return { kind: 'expired' };
  if (code === 'insufficient_scope') return { kind: 'unticked' };
  const words = code === null ? undefined : NOT_CONNECTED_CODES[code];
  if (words !== undefined) return { kind: 'not-connected', code: words };
  return { kind: 'failed', message: error.message };
}

/** What `start` is asked for. */
export interface ConnectRequest {
  orgRef: string;
  accountKind: 'personal' | 'board_account';
  /** A reconnect names its connection; core then refuses a different account. */
  reconnectConnectionId?: string | null;
  /** The address to suggest on the provider's screen (a reconnect's own account). */
  loginHint?: string | null;
  /** The console page that asked, where a full-page return goes back to. */
  returnPath: string;
}

/** The start body, for core's `oauth/start`. */
export function startBody(request: ConnectRequest, redirectUri: string) {
  return {
    provider: CALENDAR_PROVIDER,
    purpose: BOARD_MEETINGS_PURPOSE,
    account_kind: request.accountKind,
    redirect_uri: redirectUri,
    return_path: request.returnPath,
    ...(request.reconnectConnectionId
      ? { reconnect_connection_id: request.reconnectConnectionId }
      : {}),
    ...(request.loginHint ? { login_hint: request.loginHint } : {}),
  };
}

/**
 * Open the consent window synchronously, inside the click (a pop-up blocker only lets a window
 * opened by the person's own gesture through), blank and disowned. Null when the browser blocked it
 * or can't carry the answer back; the caller then sends the whole tab instead.
 */
export function openConsentWindow(): Window | null {
  if (!canUsePopup()) return null;
  const opened = window.open('', CALENDAR_CONNECT_WINDOW, 'popup,width=520,height=720');
  if (opened === null) return null;
  try {
    opened.opener = null;
  } catch {
    // A window we just opened is ours to disown; if a browser refuses, it is still blank.
  }
  return opened;
}

// ── The schedule dialog's draft, kept across a full-page trip to the provider ────────────────────

/** This tab's key for a schedule dialog's unsaved fields while the tab is away at the provider. */
export const SCHEDULE_DRAFT_KEY = 'givmo.board.schedule-draft';

/** A schedule dialog's fields, as the person left them. */
export interface ScheduleDraft {
  orgRef: string;
  /**
   * CBG-YR6-MEET: who is meeting — a committee's id, or '' for the board. Kept so a committee's
   * meeting never comes back from the provider as the board's.
   */
  committeeId: string;
  title: string;
  meetingType: string;
  timeZone: string;
  startDate: string;
  startTime: string;
  endTime: string;
  location: string;
  videoLink: string;
}

function sessionStore(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Keep the dialog's fields in this tab before it leaves for the provider. */
export function keepScheduleDraft(draft: ScheduleDraft): void {
  try {
    sessionStore()?.setItem(SCHEDULE_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Storage full or refused: the trip goes on, and the dialog opens empty on return.
  }
}

/** Whether this tab keeps a draft for one organization (without taking it). */
export function hasScheduleDraft(orgRef: string): boolean {
  try {
    const raw = sessionStore()?.getItem(SCHEDULE_DRAFT_KEY) ?? null;
    if (raw === null) return false;
    return (JSON.parse(raw) as Record<string, unknown>).orgRef === orgRef;
  } catch {
    return false;
  }
}

/** Take this tab's kept draft for one organization, once (it is removed as it is read). */
export function takeScheduleDraft(orgRef: string): ScheduleDraft | null {
  const store = sessionStore();
  let raw: string | null = null;
  try {
    raw = store?.getItem(SCHEDULE_DRAFT_KEY) ?? null;
    store?.removeItem(SCHEDULE_DRAFT_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    const field = (key: string) => (typeof value[key] === 'string' ? (value[key] as string) : '');
    if (field('orgRef') !== orgRef) return null;
    return {
      orgRef,
      committeeId: field('committeeId'),
      title: field('title'),
      meetingType: field('meetingType'),
      timeZone: field('timeZone'),
      startDate: field('startDate'),
      startTime: field('startTime'),
      endTime: field('endTime'),
      location: field('location'),
      videoLink: field('videoLink'),
    };
  } catch {
    return null;
  }
}
